import { API_BASE } from '@/utils/api-url'
import {
  AUTH_EXPIRED_ERROR_MESSAGE,
  clearWebsiteAuthSession,
  getHttpErrorMessage,
  getNetworkErrorMessage,
  isApiSuccess,
  isAuthExpiredResponse,
  readResponseJson
} from '@/utils/http-error'

/**
 * 发起官网 API 请求；FormData 请求由浏览器自动生成上传边界。
 * @param {string} path API 路径
 * @param {object} options fetch 请求配置
 * @returns {Promise<*>} 业务数据
 */
async function request(path, options = {}) {
  const token = localStorage.getItem('dzzj_website_token')
  const { headers: optionHeaders = {}, ...requestOptions } = options
  const defaultHeaders = requestOptions.body instanceof FormData
    ? {}
    : { 'Content-Type': 'application/json' }
  let response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...requestOptions,
      headers: {
        ...defaultHeaders,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...optionHeaders
      }
    })
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error))
  }
  const result = await readResponseJson(response)
  if (isAuthExpiredResponse(response, result)) {
    if (token) clearWebsiteAuthSession()
    throw new Error(getHttpErrorMessage(response, result, AUTH_EXPIRED_ERROR_MESSAGE))
  }
  if (!response.ok || !isApiSuccess(result)) {
    const fallback = response.ok ? '接口响应格式错误' : '接口请求失败'
    throw new Error(getHttpErrorMessage(response, result, fallback))
  }
  return result.data
}

export function createWebsiteOrder(data, idempotencyKey) {
  return request('/trade/website/orders', {
    method: 'POST',
    headers: {
      'Idempotency-Key': idempotencyKey
    },
    body: JSON.stringify(data)
  })
}

export function reviseWebsiteOrder(id, data) {
  return request(`/trade/website/orders/${id}/revision`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export function getWebsiteProductConfig() {
  return request('/trade/website/product-config')
}

/** 查询官网可售商品目录。 */
export function getWebsiteProducts() {
  return request('/trade/website/products')
}

/** 查询城市合伙人套餐可选的空闲合作区域。 */
export function getWebsiteCityPartnerRegions(params = {}) {
  const query = new URLSearchParams()
  if (params.keyword) query.set('keyword', params.keyword)
  if (params.provinceCode) query.set('provinceCode', params.provinceCode)
  if (params.cityCode) query.set('cityCode', params.cityCode)
  if (params.current) query.set('current', String(params.current))
  if (params.size) query.set('size', String(params.size))
  const queryString = query.toString()
  return request(`/trade/website/city-partner/regions${queryString ? `?${queryString}` : ''}`)
}

/**
 * 查询城市合伙人下单前需要确认的合同模板。
 * @param {string|number} productCode 商品编码
 * @param {object} cityPartnerRequest 企业信息与合作区域草稿
 * @returns {Promise<Array<object>>} 合同模板列表
 */
export function getWebsiteCityPartnerContractDraft(productCode, cityPartnerRequest) {
  return request('/trade/website/city-partner/contracts/draft', {
    method: 'POST',
    body: JSON.stringify({ productCode, cityPartnerRequest })
  })
}

/**
 * 下载使用当前草稿替换占位符后的城市合伙人合同预览。
 * @param {string|number} templateId 模板ID
 * @param {string|number} productCode 商品编码
 * @param {object} cityPartnerRequest 企业信息与合作区域草稿
 * @returns {Promise<{blob:Blob,filename:string}>} 合同预览文件 Blob 与文件名
 */
export async function downloadWebsiteCityPartnerContractDraft(templateId, productCode, cityPartnerRequest) {
  const token = localStorage.getItem('dzzj_website_token')
  let response
  try {
    response = await fetch(`${API_BASE}/trade/website/city-partner/contracts/draft/${templateId}/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ productCode, cityPartnerRequest })
    })
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error, '合同预览下载失败，请检查网络'))
  }
  if (!response.ok) {
    const result = await readResponseJson(response)
    if (isAuthExpiredResponse(response, result) && token) {
      clearWebsiteAuthSession()
    }
    throw new Error(getHttpErrorMessage(response, result, '合同预览下载失败'))
  }
  const disposition = response.headers.get('Content-Disposition') || ''
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  let filename = ''
  try {
    filename = decodeURIComponent(utf8Match?.[1] || '')
  } catch (error) {
    filename = utf8Match?.[1] || ''
  }
  return { blob: await response.blob(), filename: filename || '城市合伙人合同预览.docx' }
}

/**
 * 获取城市合伙人下单前合同的HTML在线预览。
 * @param {string|number} templateId 模板ID
 * @param {string|number} productCode 商品编码
 * @param {object} cityPartnerRequest 企业信息与合作区域草稿
 * @returns {Promise<object>} 合同标题与安全HTML
 */
export function previewWebsiteCityPartnerContractDraft(templateId, productCode, cityPartnerRequest) {
  return request(`/trade/website/city-partner/contracts/draft/${templateId}/preview-html`, {
    method: 'POST',
    body: JSON.stringify({ productCode, cityPartnerRequest })
  })
}

/**
 * 上传线下转账支付凭证图片。
 * @param {File} file 支付凭证图片文件
 * @returns {Promise<object>} 上传结果，包含 url 与 filename
 */
export function uploadWebsitePaymentVoucher(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request('/file/upload', {
    method: 'POST',
    body: formData
  })
}

export function getWebsiteOrders() {
  return request('/trade/website/orders')
}

export function getWebsiteOrder(id) {
  const orderId = String(id || '').trim()
  if (!/^\d+$/.test(orderId)) {
    return Promise.reject(new Error('订单ID无效'))
  }
  return request(`/trade/website/orders/${orderId}`)
}

export function createWebsiteAlipayPay(id, terminal) {
  return request(`/trade/website/orders/${id}/pay`, {
    method: 'POST',
    body: JSON.stringify({ terminal })
  })
}

/**
 * 提交线下转账核对信息与支付凭证。
 * @param {string|number} id 订单 ID
 * @param {string} terminal 支付终端
 * @param {string} paymentVoucher 支付凭证文件地址
 * @returns {Promise<object>} 更新后的订单
 */
export function submitWebsiteBankTransfer(id, terminal, paymentVoucher) {
  return request(`/trade/website/orders/${id}/bank-transfer/submit`, {
    method: 'POST',
    body: JSON.stringify({ terminal, paymentVoucher })
  })
}

export function cancelWebsiteOrder(id) {
  return request(`/trade/website/orders/${id}/cancel`, {
    method: 'PUT'
  })
}

export function confirmWebsiteReceipt(id) {
  return request(`/trade/website/orders/${id}/receipt`, {
    method: 'PUT'
  })
}

export function getWebsiteAddresses() {
  return request('/trade/website/addresses')
}

export function deleteWebsiteAddress(id) {
  return request(`/trade/website/addresses/${id}`, {
    method: 'DELETE'
  })
}

/**
 * 查询城市合伙人订单的合同进度、文件列表与寄回信息。
 * @param {string|number} orderId 订单 ID
 * @returns {Promise<object>} 合同详情
 */
export function getWebsiteOrderContracts(orderId) {
  return request(`/trade/orders/${orderId}/contracts`)
}

/**
 * 鉴权下载城市合伙人订单的已生成合同。
 * @param {string|number} orderId 订单 ID
 * @param {string|number} recordId 合同记录 ID
 * @returns {Promise<{blob:Blob,filename:string}>} 合同文件 Blob 与文件名
 */
export async function downloadWebsiteOrderContract(orderId, recordId) {
  const token = localStorage.getItem('dzzj_website_token')
  let response
  try {
    response = await fetch(`${API_BASE}/trade/orders/${orderId}/contracts/${recordId}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error, '合同下载失败，请检查网络'))
  }
  if (!response.ok) {
    const result = await readResponseJson(response)
    if (isAuthExpiredResponse(response, result) && token) {
      clearWebsiteAuthSession()
    }
    throw new Error(getHttpErrorMessage(response, result, '合同下载失败'))
  }
  const disposition = response.headers.get('Content-Disposition') || ''
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  let filename = ''
  try {
    filename = decodeURIComponent(utf8Match?.[1] || plainMatch?.[1] || '')
  } catch (error) {
    filename = utf8Match?.[1] || plainMatch?.[1] || ''
  }
  if (!filename) filename = `城市合伙人合同-${orderId}-${recordId}.docx`
  return { blob: await response.blob(), filename }
}

/**
 * 获取城市合伙人订单合同的HTML在线预览。
 * @param {string|number} orderId 订单 ID
 * @param {string|number} recordId 合同记录 ID
 * @returns {Promise<object>} 合同标题、编号与安全HTML
 */
export function previewWebsiteOrderContract(orderId, recordId) {
  return request(`/trade/orders/${orderId}/contracts/${recordId}/preview`)
}

/**
 * 填写或修改城市合伙人合同的寄回快递信息。
 * @param {string|number} orderId 订单 ID
 * @param {string} returnCourierCompany 寄回快递公司
 * @param {string} returnCourierNo 寄回快递单号
 * @returns {Promise<object>} 更新后的合同信息
 */
export function submitWebsiteContractReturnMail(orderId, returnCourierCompany, returnCourierNo) {
  return request(`/trade/orders/${orderId}/contracts/return-mail`, {
    method: 'POST',
    body: JSON.stringify({ returnCourierCompany, returnCourierNo })
  })
}
